import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import {
  ClarificationTarget,
  StepDirection,
  buildClarificationTargets,
  buildRenderOrderIndex,
  countUnresolved,
  cursorFor,
  slugsWithUnresolved,
  stepIndex,
  visibilityDependencySlugs,
} from './clarificationTargets';
import { FormFieldValue, FormTreeData, QuestionData } from './types';

/**
 * Two contexts, one provider:
 *
 * - FormDisclosureContext — which top-level sections are expanded and which
 *   clarification dialog is open. Consumed by every FormGroup and
 *   ClarificationIcon, so it only changes on a click, never on scroll.
 * - ClarificationNavigationContext — the ordered target list and the user's
 *   position in it (tracked on scroll). Consumed by the navigator bar, the
 *   dialog stepper and the sidebar only.
 *
 * Both were lifted out of FormGroup/ClarificationIcon so navigation can
 * expand a collapsed section and open the next field's dialog.
 */

interface FormDisclosureValue {
  isSectionOpen: (sectionId: string) => boolean;
  toggleSection: (sectionId: string) => void;
  /** Expand All / Collapse All — also clears per-section overrides */
  setAllSectionsOpen: (open: boolean) => void;
  openDialogSlug: string | null;
  setOpenDialogSlug: (slug: string | null) => void;
}

interface NavigateOptions {
  /** Open the target's clarification dialog on arrival */
  openDialog?: boolean;
  /** Step relative to this question instead of the tracked position */
  fromSlug?: string | null;
}

interface ClarificationNavigationValue {
  enabled: boolean;
  targets: ClarificationTarget[];
  /**
   * Unresolved threads on questions that aren't navigable — hidden by
   * branching (or not on this form). Still open on the server, so they must
   * keep "All clarifications resolved" from showing.
   */
  hiddenUnresolvedCount: number;
  /** See cursorFor — integral when on a target, x.5 when between targets */
  cursorFor: (slug: string | null) => number;
  currentSlug: string | null;
  goTo: (index: number, options?: NavigateOptions) => void;
  step: (direction: StepDirection, options?: NavigateOptions) => void;
  /** Lands on the section's first open clarification; false if it has none */
  goToFirstInSection: (sectionId: string) => boolean;
  /** Expands the section if collapsed, then scrolls to its top */
  goToSection: (sectionId: string) => void;
}

const HIGHLIGHT_ATTRIBUTE = 'data-clarification-highlight';
const HIGHLIGHT_MS = 1500;
// Long enough for a smooth scroll to settle, so the scroll tracker doesn't
// walk the position through every target the page passes on the way.
const PROGRAMMATIC_SCROLL_MS = 1000;

const escapeAttributeValue = (value: string): string =>
  typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');

const questionAnchor = (slug: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[data-question-anchor="${escapeAttributeValue(slug)}"]`
  );

const sectionCard = (sectionId: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[data-section-anchor="${escapeAttributeValue(sectionId)}"]`
  );

const clarificationTrigger = (slug: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[data-clarification-trigger="${escapeAttributeValue(slug)}"]`
  );

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const scrollSectionIntoView = (sectionId: string) => {
  document.getElementById(`section-${sectionId}`)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const FormDisclosureContext = createContext<FormDisclosureValue | null>(null);

// Without a provider (sidebar rendered on its own, e.g. in tests) section
// clicks keep their original behaviour: scroll to the section, nothing else.
const fallbackNavigation: ClarificationNavigationValue = {
  enabled: false,
  targets: [],
  hiddenUnresolvedCount: 0,
  cursorFor: () => -0.5,
  currentSlug: null,
  goTo: () => {},
  step: () => {},
  goToFirstInSection: () => false,
  goToSection: scrollSectionIntoView,
};

const ClarificationNavigationContext =
  createContext<ClarificationNavigationValue>(fallbackNavigation);

type PendingLanding =
  | { kind: 'question'; slug: string; openDialog: boolean }
  | { kind: 'section'; sectionId: string };

interface ClarificationNavigationProviderProps {
  children: React.ReactNode;
  formTree: FormTreeData[];
  questionsBySlug: ReadonlyMap<string, QuestionData>;
  /** Whether the user can see clarifications on this submission at all */
  enabled: boolean;
}

export const ClarificationNavigationProvider = ({
  children,
  formTree,
  questionsBySlug,
  enabled,
}: ClarificationNavigationProviderProps) => {
  const { clarificationsByQuestion } = useClarificationsContext();

  const [allSectionsOpen, setAllSectionsOpenState] = useState(true);
  const [sectionOverrides, setSectionOverrides] = useState<
    Record<string, boolean>
  >({});
  const [openDialogSlug, setOpenDialogSlugState] = useState<string | null>(
    null
  );
  const [currentSlug, setCurrentSlug] = useState<string | null>(null);
  const [pendingLanding, setPendingLanding] = useState<PendingLanding | null>(
    null
  );
  const programmaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number>();
  const highlightRef = useRef<{ element: HTMLElement; timer: number } | null>(
    null
  );

  // --- Targets -------------------------------------------------------------

  // Watch only the answers that gate a question with open threads, so the
  // provider doesn't re-render on every keystroke in the form.
  const candidateSlugs = useMemo(
    () => (enabled ? slugsWithUnresolved(clarificationsByQuestion) : []),
    [enabled, clarificationsByQuestion]
  );
  const watchedSlugs = useMemo(
    () => visibilityDependencySlugs(candidateSlugs, questionsBySlug),
    [candidateSlugs, questionsBySlug]
  );

  // Not useWatch: it reads its value once at mount and doesn't re-read when
  // `name` changes. Here the slugs start empty (clarifications load after the
  // form) and grow later, so useWatch left every gating answer undefined and
  // branch questions counted as hidden. Re-read whenever the list changes,
  // and on any change to a watched answer (or a form reset, which has no name).
  const { getValues, watch } = useFormContext();
  const readWatchedValues = useCallback(
    (): Record<string, FormFieldValue> =>
      Object.fromEntries(
        watchedSlugs.map(slug => [slug, getValues(slug) as FormFieldValue])
      ),
    [getValues, watchedSlugs]
  );
  const [formValues, setFormValues] = useState(readWatchedValues);
  useEffect(() => {
    setFormValues(readWatchedValues());
    if (watchedSlugs.length === 0) return;
    const watched = new Set(watchedSlugs);
    const subscription = watch((_values, { name }) => {
      if (!name || watched.has(name)) setFormValues(readWatchedValues());
    });
    return () => subscription.unsubscribe();
  }, [watch, readWatchedValues, watchedSlugs]);

  const targets = useMemo(() => {
    if (!enabled) return [];
    return buildClarificationTargets(
      formTree,
      clarificationsByQuestion,
      formValues,
      questionsBySlug
    );
  }, [
    enabled,
    formTree,
    clarificationsByQuestion,
    questionsBySlug,
    formValues,
  ]);

  const hiddenUnresolvedCount = useMemo(() => {
    if (!enabled) return 0;
    const allUnresolved = Object.values(clarificationsByQuestion).reduce(
      (sum, clarifications) => sum + countUnresolved(clarifications),
      0
    );
    const navigable = targets.reduce((sum, t) => sum + t.unresolvedCount, 0);
    return allUnresolved - navigable;
  }, [enabled, clarificationsByQuestion, targets]);

  const renderOrder = useMemo(
    () => buildRenderOrderIndex(formTree),
    [formTree]
  );

  const cursorForSlug = useCallback(
    (slug: string | null) => cursorFor(targets, slug, renderOrder),
    [targets, renderOrder]
  );

  // --- Disclosure ----------------------------------------------------------

  const isSectionOpen = useCallback(
    (sectionId: string) => sectionOverrides[sectionId] ?? allSectionsOpen,
    [sectionOverrides, allSectionsOpen]
  );

  const toggleSection = useCallback(
    (sectionId: string) =>
      setSectionOverrides(prev => ({
        ...prev,
        [sectionId]: !(prev[sectionId] ?? allSectionsOpen),
      })),
    [allSectionsOpen]
  );

  const expandSection = useCallback(
    (sectionId: string) =>
      setSectionOverrides(prev =>
        (prev[sectionId] ?? allSectionsOpen)
          ? prev
          : { ...prev, [sectionId]: true }
      ),
    [allSectionsOpen]
  );

  const setAllSectionsOpen = useCallback((open: boolean) => {
    setAllSectionsOpenState(open);
    setSectionOverrides({});
  }, []);

  const setOpenDialogSlug = useCallback((slug: string | null) => {
    setOpenDialogSlugState(slug);
    // Opening a field's thread by hand makes it the current position too, so
    // "Next" after closing the dialog continues from there.
    if (slug !== null) setCurrentSlug(slug);
  }, []);

  // --- Navigation ----------------------------------------------------------

  const goTo = useCallback(
    (index: number, options: NavigateOptions = {}) => {
      const target = targets[index];
      if (!target) return;
      const openDialog = options.openDialog ?? false;
      setCurrentSlug(target.questionSlug);
      expandSection(target.topLevelSectionId);
      setOpenDialogSlugState(openDialog ? target.questionSlug : null);
      setPendingLanding({
        kind: 'question',
        slug: target.questionSlug,
        openDialog,
      });
    },
    [targets, expandSection]
  );

  const step = useCallback(
    (direction: StepDirection, options: NavigateOptions = {}) => {
      const from =
        options.fromSlug !== undefined ? options.fromSlug : currentSlug;
      const index = stepIndex(cursorForSlug(from), targets.length, direction);
      goTo(index, options);
    },
    [currentSlug, cursorForSlug, targets.length, goTo]
  );

  const goToFirstInSection = useCallback(
    (sectionId: string) => {
      const index = targets.findIndex(t => t.topLevelSectionId === sectionId);
      if (index === -1) return false;
      goTo(index);
      return true;
    },
    [targets, goTo]
  );

  const goToSection = useCallback(
    (sectionId: string) => {
      expandSection(sectionId);
      setPendingLanding({ kind: 'section', sectionId });
    },
    [expandSection]
  );

  // Runs after the commit that expanded the target's section, so the anchor
  // is in the DOM by now.
  useEffect(() => {
    if (!pendingLanding) return;

    programmaticScrollRef.current = true;
    window.clearTimeout(programmaticScrollTimerRef.current);
    programmaticScrollTimerRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
    }, PROGRAMMATIC_SCROLL_MS);

    if (pendingLanding.kind === 'section') {
      scrollSectionIntoView(pendingLanding.sectionId);
      return;
    }

    const anchor = questionAnchor(pendingLanding.slug);
    if (!anchor) return;
    anchor.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });

    // Imperative on purpose: a transient ring shouldn't re-render the form.
    if (highlightRef.current) {
      window.clearTimeout(highlightRef.current.timer);
      highlightRef.current.element.removeAttribute(HIGHLIGHT_ATTRIBUTE);
    }
    anchor.setAttribute(HIGHLIGHT_ATTRIBUTE, 'true');
    const timer = window.setTimeout(() => {
      anchor.removeAttribute(HIGHLIGHT_ATTRIBUTE);
      highlightRef.current = null;
    }, HIGHLIGHT_MS);
    highlightRef.current = { element: anchor, timer };

    // Land beside the field, not in a dialog over it — the customer usually
    // needs to edit the answer. Focus the badge so Enter opens the thread.
    if (!pendingLanding.openDialog) {
      clarificationTrigger(pendingLanding.slug)?.focus({
        preventScroll: true,
      });
    }
  }, [pendingLanding]);

  useEffect(
    () => () => {
      window.clearTimeout(programmaticScrollTimerRef.current);
      if (highlightRef.current) {
        window.clearTimeout(highlightRef.current.timer);
      }
    },
    []
  );

  // Track position while the user scrolls on their own: the current target is
  // the last one whose top has passed the middle of the viewport. Targets in a
  // collapsed section have no anchor, so their section card stands in.
  useEffect(() => {
    if (!enabled || targets.length === 0) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      if (programmaticScrollRef.current) return;
      const middle = window.innerHeight / 2;
      let passed: string | null = null;
      for (const target of targets) {
        const element =
          questionAnchor(target.questionSlug) ??
          sectionCard(target.topLevelSectionId);
        if (!element) continue;
        if (element.getBoundingClientRect().top > middle) break;
        passed = target.questionSlug;
      }
      setCurrentSlug(passed);
    };
    const onScroll = (event: Event) => {
      // Scrolling inside an open dialog is not page movement
      if (
        event.target instanceof Element &&
        event.target.closest('[role="dialog"]')
      ) {
        return;
      }
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled, targets]);

  const disclosureValue = useMemo<FormDisclosureValue>(
    () => ({
      isSectionOpen,
      toggleSection,
      setAllSectionsOpen,
      openDialogSlug,
      setOpenDialogSlug,
    }),
    [
      isSectionOpen,
      toggleSection,
      setAllSectionsOpen,
      openDialogSlug,
      setOpenDialogSlug,
    ]
  );

  const navigationValue = useMemo<ClarificationNavigationValue>(
    () => ({
      enabled,
      targets,
      hiddenUnresolvedCount,
      cursorFor: cursorForSlug,
      currentSlug,
      goTo,
      step,
      goToFirstInSection,
      goToSection,
    }),
    [
      enabled,
      targets,
      hiddenUnresolvedCount,
      cursorForSlug,
      currentSlug,
      goTo,
      step,
      goToFirstInSection,
      goToSection,
    ]
  );

  return (
    <FormDisclosureContext.Provider value={disclosureValue}>
      <ClarificationNavigationContext.Provider value={navigationValue}>
        {children}
      </ClarificationNavigationContext.Provider>
    </FormDisclosureContext.Provider>
  );
};

/** null outside a ClarificationNavigationProvider — callers keep local state */
export const useFormDisclosure = (): FormDisclosureValue | null =>
  useContext(FormDisclosureContext);

export const useClarificationNavigation = (): ClarificationNavigationValue =>
  useContext(ClarificationNavigationContext);
